import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Area, Bar, CartesianGrid, ComposedChart, Line, ReferenceDot, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useSession } from "../../session/SessionContext";
import styles from "./ConsumptionSummary.module.css";
import { Tooltip as HoverTooltip } from "../shared/Tooltip";
import { formatAge, formatCompactCount, formatCount, formatDate, formatMoney } from "./formatters";
import { ThresholdBanner } from "./ThresholdBanner";
import { PartialState } from "../states";
import { getSeriesColor } from "../charts/chartColors";
import { useSeriesVisibility } from "../charts/useSeriesVisibility";
import { SeriesLegend, type SeriesLegendEntry } from "../charts/SeriesLegend";
import { ChartDefs, PulseDot } from "../charts/chartEffects";
import { useChartEffectIds } from "../charts/useChartEffectIds";
import { VendorIcon } from "./VendorIcon";
import { type HistoryPeriodResponse, type MyConsumptionResponse, type MyDailySpendResponse, type VendorConsumptionResponse } from "../../api/reporting";
import { isCurrentPeriod } from "../../hooks/useSelectedPeriod";
import { UsageHero } from "./UsageHero";
import { SpendCalendar } from "./SpendCalendar";
import { MonthComparison, type PreviousTotal } from "./MonthComparison";
import { projectMonthEnd, todayKeyFor } from "./usagePace";
import { spotlightPointerMove } from "../../utils/spotlight";

/**
 * The "My consumption" landing screen's main content (phase 12, `AIA-81`, `SC-1`): the consolidated
 * figure first and large, the per-tool breakdown with each figure's age, the seat-versus-consumption
 * allowance split (`CON-15`), burn rate and projected exhaustion with their basis stated, and a deep
 * link out to each vendor's own view (`UR-E-20`). Rendered only once the caller has a loaded
 * response — the loading, empty, error and denied states are the page's job, not this component's.
 *
 * @param props.response - The loaded consumption response.
 * @param props.onDismissThreshold - Called with the acknowledged threshold when the banner is dismissed.
 * @param props.dismissingThreshold - Whether a dismissal request is in flight.
 * @param props.previousPeriod - The month immediately before this one from the history feed, or null when it is not available.
 * @param props.subjectName - Who the figures belong to, when a manager is looking at someone else's usage. The page is then read
 * only: the threshold banner (which only the person can dismiss) is left out, and the words say "their" rather than "your".
 */
export function ConsumptionSummary({
  response,
  onDismissThreshold,
  dismissingThreshold,
  effectiveAllowanceUsd,
  previousPeriod,
  subjectName,
}: {
  response: MyConsumptionResponse;
  onDismissThreshold: (thresholdPercent: number) => void;
  dismissingThreshold: boolean;
  effectiveAllowanceUsd: number | null;
  previousPeriod: HistoryPeriodResponse | null;
  subjectName?: string;
}): React.JSX.Element {
   const { t } = useTranslation();
   const { snapshot } = useSession();
   const canSeeOperationalWarnings =
     snapshot.status === "signed-in" &&
     [
       "consumption.read.org",
       "consumption.read.others",
       "identity.impersonate",
       "settings.write",
       "audit.read",
       "roles.administer",
       "entitlement.administer",
       "accounts.administer",
       "request.approve",
     ].some((permission) => snapshot.permissions.includes(permission));

   const bannerVendorName =
     response.vendors.find((vendor) => vendor.vendorId === response.pendingThresholdBanner?.vendorId)?.displayName ?? "";
  const isCurrentMonth = isCurrentPeriod(response.periodStart);

  // "Which tools I hold" (UR-E-12): a vendor with a reading this period, or a currently-effective
  // allowance, is one this person holds. A vendor with neither is not shown — there is nothing
  // honest to say about a tool nobody has evidence this person uses.
  const heldVendorIds = new Set<string>([
    ...response.vendors.filter((vendor) => vendor.hasReading).map((vendor) => vendor.vendorId),
    ...response.allowances.map((allowance) => allowance.vendorId),
  ]);
  const heldVendors = response.vendors.filter((vendor) => heldVendorIds.has(vendor.vendorId));
  // Each card's accent is its series colour in the forecast chart, which colours the vendors with a reading in this order.
  const chartVendorIds = response.vendors.filter((vendor) => vendor.hasReading).map((vendor) => vendor.vendorId);

  // Delivered inside the consumption response so the consolidated forecast chart and every
  // per-vendor mini chart share the same daily actuals without a second request.
  const dailySpend: MyDailySpendResponse | null = response.dailySpend
    ? { personId: response.personId, from: response.periodStart, to: response.periodEnd, days: response.dailySpend }
    : null;
  const loadingDailySpend = false;

  return (
    <div onPointerMove={spotlightPointerMove}>
      <ThresholdBanner
        key={response.pendingThresholdBanner ? `${response.pendingThresholdBanner.vendorId}-${String(response.pendingThresholdBanner.thresholdPercent)}` : "none"}
        banner={isCurrentMonth && subjectName === undefined ? response.pendingThresholdBanner : null}
        vendorDisplayName={bannerVendorName}
        onDismiss={() => {
          if (response.pendingThresholdBanner) {
            onDismissThreshold(response.pendingThresholdBanner.thresholdPercent);
          }
        }}
        dismissing={dismissingThreshold}
      />

      {canSeeOperationalWarnings && response.isIncomplete && (
        <PartialState
          vendorName={response.incompleteVendorIds
            .map((id) => response.vendors.find((vendor) => vendor.vendorId === id)?.displayName ?? id)
            .join(", ")}
        />
      )}

      {response.consolidated && (
        <>
          <UsageHero
            response={response}
            effectiveAllowanceUsd={effectiveAllowanceUsd}
            isCurrentMonth={isCurrentMonth}
            previousPeriod={previousPeriod}
            subjectName={subjectName}
          />
          <div className={dailySpend && dailySpend.days.length > 0 ? styles.insights : undefined}>
            <UsageProjectionChart response={response} dailySpend={dailySpend} loadingDailySpend={loadingDailySpend} isCurrentMonth={isCurrentMonth} />
            {dailySpend && dailySpend.days.length > 0 && (
              <SpendCalendar periodStart={response.periodStart} periodEnd={response.periodEnd} generatedAt={response.generatedAt} days={dailySpend.days} />
            )}
          </div>
        </>
      )}

      {/* No visible heading: the cards say what they are. The label keeps the region named for screen readers. */}
      <section aria-label={t("consumption.vendors.title")} className={styles.vendorSection}>
        <ul className={styles.vendorList} style={vendorColumns(heldVendors.length)}>
          {heldVendors.map((vendor, index) => (
            <VendorCard
              key={vendor.vendorId}
              index={index}
              accentColor={chartVendorIds.includes(vendor.vendorId) ? getSeriesColor(chartVendorIds.indexOf(vendor.vendorId)) : null}
              vendor={vendor}
              response={response}
              dailySpend={dailySpend}
              loadingDailySpend={loadingDailySpend}
              isCurrentMonth={isCurrentMonth}
              previousPeriod={previousPeriod}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}

/** One point on a forecast chart's X axis: a day in the period, with its actual/cumulative/projected values. */
interface ForecastChartPoint {
  day: string;
  label: string;
  actual: number;
  cumulative: number | null;
  projected: number | null;
  isToday: boolean;
}

/** The last Monday-to-Friday day on or before `periodEnd`, as a `YYYY-MM-DD` key. */
function lastWorkingDayOf(periodEnd: string): string {
  const date = new Date(`${periodEnd}T00:00:00Z`);
  while (date.getUTCDay() === 0 || date.getUTCDay() === 6) {
    date.setUTCDate(date.getUTCDate() - 1);
  }
  return date.toISOString().slice(0, 10);
}

/**
 * The run-out date a forecast chart may show, or null when none should be shown. The allowance
 * renews on the 1st, so a projected run-out on or after the period's last working day is treated as
 * lasting to month end and is not shown: no "Runs out" marker and no "could run out" note. A date
 * already in the past is always kept, because it describes what happened rather than a projection.
 */
function visibleRunOutDate(projectedDate: string | null, periodEnd: string, currentDateKey: string): string | null {
  if (!projectedDate) {
    return null;
  }
  if (projectedDate < currentDateKey) {
    return projectedDate;
  }
  return projectedDate >= lastWorkingDayOf(periodEnd) ? null : projectedDate;
}

/**
 * Builds the shared day-by-day series both the consolidated forecast chart and each vendor's mini
 * chart plot: actual spend so far, the running total, and (from today onward) the projection to
 * period end using the server's weekday and weekend rates (the standard projection: separate
 * averages over the last 7 complete days). Kept as one function so the two charts can
 * never silently drift into different projection logic.
 */
function buildForecastSeries(params: {
  periodStart: string;
  periodEnd: string;
  generatedAt: string;
  amount: number;
  weekdayRatePerDay: number;
  weekendRatePerDay: number;
  projectedExhaustionDate: string | null;
  dailyAmounts: Map<string, number>;
  /** The period's seat fees, part of `amount` but not of the daily usage: the running total starts at them. */
  seatFees: number;
}): {
  chartData: ForecastChartPoint[];
  daysInPeriod: number;
  hasActualSpendSeries: boolean;
  chartReferenceX: string | null;
  todayX: string;
} {
  const periodStart = new Date(`${params.periodStart}T00:00:00Z`);
  const periodEnd = new Date(`${params.periodEnd}T00:00:00Z`);
  const generatedAt = new Date(params.generatedAt);
  const currentPoint = generatedAt > periodEnd ? periodEnd : generatedAt;
  const currentDateKey = currentPoint.toISOString().slice(0, 10);
  const daysInPeriod = Math.max(1, Math.round((periodEnd.getTime() - periodStart.getTime()) / 86400000) + 1);
  const observedDays = Math.max(0, Math.round((currentPoint.getTime() - periodStart.getTime()) / 86400000));

  const hasActualSpendSeries = Array.from(params.dailyAmounts.values()).some((amount) => amount > 0);

  let cumulativeActual = params.seatFees;
  let projectedSpend = params.amount;
  const chartData: ForecastChartPoint[] = Array.from({ length: daysInPeriod }, (_, index) => {
    const date = new Date(periodStart.getTime() + index * 86400000);
    const dateKey = date.toISOString().slice(0, 10);
    if (index > observedDays) {
      const dayOfWeek = date.getUTCDay();
      projectedSpend += dayOfWeek === 0 || dayOfWeek === 6 ? params.weekendRatePerDay : params.weekdayRatePerDay;
    }
    const actual = params.dailyAmounts.get(dateKey) ?? 0;
    const isTodayOrEarlier = dateKey <= currentDateKey;
    const isTodayOrLater = dateKey >= currentDateKey;

    if (isTodayOrEarlier) {
      cumulativeActual += actual;
    }

    return {
      day: dateKey,
      label: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      actual: isTodayOrEarlier ? actual : 0,
      cumulative: isTodayOrEarlier ? cumulativeActual : null,
      // Starts at today (rather than tomorrow) so the projected line picks up right where the
      // total spend line leaves off, instead of leaving a gap at the current date.
      projected: isTodayOrLater ? Math.max(projectedSpend, params.amount) : null,
      isToday: dateKey === currentDateKey,
    };
  });

  return {
    chartData,
    daysInPeriod,
    hasActualSpendSeries,
    chartReferenceX: visibleRunOutDate(params.projectedExhaustionDate, params.periodEnd, currentDateKey),
    todayX: currentDateKey,
  };
}

/** One entry recharts passes a tooltip's `content` render prop for each visible series. */
interface ForecastTooltipEntry {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string | (number | string)[];
  color?: string;
}

/**
 * The tooltip both {@link UsageProjectionChart} and {@link VendorMiniChart} use: one line per
 * visible series (actual/cumulative/projected), each labelled and formatted as money in the given
 * currency. Kept as a standalone component (rather than inline in each chart's `content` prop) so
 * neither chart re-creates this renderer on every render.
 *
 * A series filled with a gradient reports its fill, a `url(#…)` reference, as its colour, which
 * cannot colour the row's square; `colors` gives the series' own colour by data key instead.
 *
 * @param props.colors - Each series' colour by data key, for series whose reported colour is not one.
 * @param props.order - The data keys in the order their rows are listed; others follow, as given.
 * @param props.showLabel - Whether to head the tooltip with the point's label (the day).
 */
function ForecastTooltip({
  active,
  payload,
  label: pointLabel,
  currency,
  colors,
  order,
  showLabel = false,
}: {
  active?: boolean;
  payload?: ForecastTooltipEntry[];
  label?: string | number;
  currency: string;
  colors?: ReadonlyMap<string, string>;
  order?: readonly string[];
  showLabel?: boolean;
}): React.JSX.Element | null {
  const { t } = useTranslation();

  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const rank = (entry: ForecastTooltipEntry): number => {
    const index = order ? order.indexOf(String(entry.dataKey)) : -1;
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  const entries = order ? [...payload].sort((a, b) => rank(a) - rank(b)) : payload;

  return (
    <div className="chart-tooltip">
      {showLabel && pointLabel !== undefined && <p className="chart-tooltip-label">{String(pointLabel)}</p>}
      <ul>
        {entries.map((entry) => {
          const dataKey = entry.dataKey;
          let label: string = typeof entry.name === "string" ? entry.name : String(entry.name);

          if (entry.value === null || entry.value === undefined) {
            return null;
          }

          if (dataKey === "actual") {
            label = t("consumption.period.dailySpend");
          } else if (dataKey === "cumulative") {
            label = t("consumption.period.totalSpend");
          } else if (dataKey === "projected") {
            label = t("consumption.period.projected");
          }

          return (
            <li key={String(dataKey)} style={{ color: colors?.get(String(dataKey)) ?? entry.color }}>
              <span className="chart-tooltip-name">{String(label)}</span>
              <span className="chart-tooltip-value">{formatMoney(Number(entry.value), currency)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** One day's chart point on the "My Usage" stacked chart: a spend value per vendor, the running total up to and including that day, and (from today onward) the projected running total to period end. */
type UsageChartPoint = Record<string, number | string | null> & {
  label: string;
  day: string;
  cumulative: number | null;
  projected: number | null;
};

function UsageProjectionChart({
  response,
  dailySpend,
  loadingDailySpend,
  isCurrentMonth,
}: {
  response: MyConsumptionResponse;
  dailySpend: MyDailySpendResponse | null;
  loadingDailySpend: boolean;
  isCurrentMonth: boolean;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const { isHidden, toggle } = useSeriesVisibility();
  const effects = useChartEffectIds();
  const consolidated = response.consolidated;

  if (!consolidated) {
    return null;
  }

  const hasActualSpendSeries = (dailySpend?.days ?? []).some((day) => day.totalAmount > 0);

  if (!dailySpend || !Array.isArray(dailySpend.days) || dailySpend.days.length === 0) {
    return (
      <div className={styles.forecastPanel} data-spotlight>
        <h3 className={`panel-title ${styles.panelTitle}`}>{t("consumption.period.forecast")}</h3>
        <div className={styles.chartContainer} style={{ minHeight: 280 }} role="img" aria-label={t("consumption.period.chartLabel")} />
        {!loadingDailySpend && <p className={styles.forecastNote}>{t("consumption.period.noDailyActuals")}</p>}
      </div>
    );
  }

  // Every provider seen across the period becomes its own stacked bar, keyed by vendor ID in the
  // same order as `response.vendors` (and therefore the vendor cards rendered below) rather than
  // the order vendors happen to first appear across `dailySpend.days`, for the same reason as the
  // "My Groups" chart this mirrors: that order is unstable and would otherwise drift out of sync
  // with the palette convention (pink, blue, yellow, ...) and the vendor cards beneath the chart.
  const vendorIds = response.vendors.filter((vendor) => vendor.hasReading).map((vendor) => vendor.vendorId);
  const vendorNamesById = new Map(response.vendors.map((vendor) => [vendor.vendorId, vendor.displayName]));

  const periodEndDate = new Date(`${response.periodEnd}T00:00:00Z`);
  const generatedAtDate = new Date(response.generatedAt);
  const currentPoint = generatedAtDate > periodEndDate ? periodEndDate : generatedAtDate;
  const currentDateKey = currentPoint.toISOString().slice(0, 10);

  const weekdayRatePerDay = consolidated.weekdayRatePerDay ?? consolidated.burnRatePerDay;
  const weekendRatePerDay = consolidated.weekendRatePerDay ?? consolidated.burnRatePerDay;

  // The days are usage alone, so the running total starts at the period's seat fees, which the total already includes.
  let runningTotal = consolidated.seatFeeAmount ?? 0;
  let projectedSpend = consolidated.amount;
  const chartData: UsageChartPoint[] = dailySpend.days.map((day) => {
    const isTodayOrEarlier = day.date <= currentDateKey;
    const isTodayOrLater = day.date >= currentDateKey;
    if (isTodayOrEarlier) {
      runningTotal += day.totalAmount;
    } else {
      const dayOfWeek = new Date(`${day.date}T00:00:00Z`).getUTCDay();
      projectedSpend += dayOfWeek === 0 || dayOfWeek === 6 ? weekendRatePerDay : weekdayRatePerDay;
    }

    const point: UsageChartPoint = {
      label: formatDate(day.date),
      day: day.date,
      cumulative: isTodayOrEarlier ? runningTotal : null,
      // Starts at today (rather than tomorrow) so the projected line picks up right where the
      // running-total line leaves off, instead of leaving a gap at the current date.
      projected: isTodayOrLater ? Math.max(projectedSpend, consolidated.amount) : null,
    };
    for (const vendorId of vendorIds) {
      point[vendorId] = day.vendors.find((vendor) => vendor.vendorId === vendorId)?.amount ?? 0;
    }

    return point;
  });

  const daysInPeriod = chartData.length;
  // The axis names the day without the year, which the period already states, so four ticks fit on a phone; the tooltip keeps the full date.
  const tickLabels = new Map(
    chartData.map((point) => [point.label, new Date(`${point.day}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })]),
  );
  const runOutDate = visibleRunOutDate(consolidated.projectedExhaustionDate, response.periodEnd, currentDateKey);
  const chartReferenceX = runOutDate;
  const exactRunsOutIndex = chartReferenceX ? chartData.findIndex((point) => point.day === chartReferenceX) : -1;
  const runsOutIndex = chartReferenceX ? (exactRunsOutIndex >= 0 ? exactRunsOutIndex : chartData.length - 1) : -1;

  // As on "My Groups", an explicit legend keeps color/order agreement with the vendor cards below
  // (Recharts' auto legend can silently reorder a stacked bar chart's entries). "Runs out" is
  // deliberately left out of both the legend entries and the tooltip's dataKeyOrder - it's a
  // point-in-time marker, not a series, and giving it a legend/tooltip slot would let it shift the
  // vendor/total/projected ordering those are built to protect.
  const legendEntries: SeriesLegendEntry[] = [
    ...vendorIds.map((vendorId, index) => ({
      key: vendorId,
      label: vendorNamesById.get(vendorId) ?? vendorId,
      color: getSeriesColor(index),
    })),
    {
      key: "cumulative",
      label: t("consumption.period.totalSpend"),
      color: getSeriesColor(vendorIds.length),
      shape: "line" as const,
    },
    ...(isCurrentMonth ? [{
      key: "projected",
      label: t("consumption.period.projected"),
      color: getSeriesColor(vendorIds.length + 1),
      shape: "line" as const,
    }] : []),
  ];
  const tooltipOrder = [...vendorIds, "cumulative", ...(isCurrentMonth ? ["projected"] : [])];
  const tooltipColors = new Map(legendEntries.map((entry) => [entry.key, entry.color]));
  // The points that matter now: today's running total, and on the current month where the projection meets the run-out date.
  const todayPoint = isCurrentMonth ? chartData.find((point) => point.day === currentDateKey) : undefined;
  const todayTotal = todayPoint?.cumulative ?? null;
  const runsOutPoint = isCurrentMonth && runsOutIndex >= 0 ? chartData[runsOutIndex] : undefined;
  const runsOutValue = runsOutPoint ? (runsOutPoint.projected ?? runsOutPoint.cumulative) : null;

  return (
    <div className={styles.forecastPanel} data-spotlight>
      <h3 className={`panel-title ${styles.panelTitle}`}>{t("consumption.period.forecast")}</h3>
      <div className={styles.chartContainer} role="img" aria-label={t("consumption.period.chartLabel")}>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData} margin={{ top: 24, right: 16, bottom: 20, left: 8 }}>
            <ChartDefs
              ids={effects}
              gradients={[
                ...vendorIds.map((vendorId, index) => ({ key: vendorId, color: getSeriesColor(index), top: 1, bottom: 0.55 })),
                { key: "cumulative", color: getSeriesColor(vendorIds.length), top: 0.28, bottom: 0 },
              ]}
            />
            <CartesianGrid vertical={false} stroke="color-mix(in srgb, var(--color-border-default) 70%, transparent)" strokeDasharray="4 4" />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }}
              tickFormatter={(label: string) => tickLabels.get(label) ?? label}
              interval={Math.max(0, Math.ceil(daysInPeriod / 4) - 1)}
            />
            <YAxis
              yAxisId="daily"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }}
              tickFormatter={(value: number) => `$${Math.round(value)}`}
              width={48}
            />
            <YAxis
              yAxisId="total"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }}
              tickFormatter={(value: number) => `$${Math.round(value)}`}
              width={48}
            />
            <Tooltip
              content={<ForecastTooltip currency={consolidated.currency} colors={tooltipColors} order={tooltipOrder} showLabel />}
              cursor={{ fill: "color-mix(in srgb, var(--color-chart-blue) 10%, transparent)" }}
            />
            {vendorIds.map((vendorId, index) => (
              <Bar
                key={vendorId}
                yAxisId="daily"
                stackId="vendors"
                dataKey={vendorId}
                name={vendorNamesById.get(vendorId) ?? vendorId}
                fill={`url(#${effects.gradient(vendorId)})`}
                // Only the top of the stack is rounded; a rounded segment inside it would leave a notch.
                radius={index === vendorIds.length - 1 ? [3, 3, 0, 0] : 0}
                hide={isHidden(vendorId)}
              />
            ))}
            <Area
              yAxisId="total"
              type="monotone"
              dataKey="cumulative"
              name={t("consumption.period.totalSpend")}
              stroke={getSeriesColor(vendorIds.length)}
              strokeWidth={2.5}
              fill={`url(#${effects.gradient("cumulative")})`}
              filter={`url(#${effects.glow})`}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--color-page-bg)" }}
              connectNulls
              hide={isHidden("cumulative")}
            />
            {isCurrentMonth && <Line
              yAxisId="total"
              type="monotone"
              dataKey="projected"
              name={t("consumption.period.projected")}
              stroke={getSeriesColor(vendorIds.length + 1)}
              strokeWidth={2.5}
              strokeDasharray="6 4"
              strokeLinecap="round"
              filter={`url(#${effects.glow})`}
              dot={false}
              connectNulls
              hide={isHidden("projected")}
            />}
            {todayPoint && todayTotal !== null && !isHidden("cumulative") && (
              <ReferenceDot yAxisId="total" x={todayPoint.label} y={todayTotal} fill={getSeriesColor(vendorIds.length)} shape={PulseDot} />
            )}
            {isCurrentMonth && runsOutIndex >= 0 && (
              <ReferenceLine
                yAxisId="total"
                x={chartData[runsOutIndex].label}
                stroke="var(--color-warning-text)"
                strokeDasharray="4 4"
                label={{ value: t("consumption.period.runsOut"), position: "top", fill: "var(--color-warning-text)", fontSize: 11 }}
              />
            )}
            {runsOutPoint && runsOutValue !== null && !isHidden("projected") && (
              <ReferenceDot yAxisId="total" x={runsOutPoint.label} y={runsOutValue} fill="var(--color-warning-text)" shape={PulseDot} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <SeriesLegend entries={legendEntries} isHidden={isHidden} onToggle={toggle} />
      {!loadingDailySpend && !hasActualSpendSeries && <p className={styles.forecastNote}>{t("consumption.period.noDailyActuals")}</p>}
      {isCurrentMonth && runOutDate && (
        <p className={styles.forecastNote}>
          {t(
            runOutDate < new Date().toISOString().slice(0, 10)
              ? "consumption.period.ranOut"
              : "consumption.period.runout",
            { date: formatDate(runOutDate) },
          )}
        </p>
      )}
    </div>
  );
}

/**
 * The figure a vendor card compares with last month: the actual total on a past month, or the
 * projected month end on the current one, using the same weekday and weekend rates as the vendor's
 * mini chart. Null when the vendor has no amount, or on the current month no rate to project with.
 *
 * @param vendor - The vendor's figures for this period.
 * @param response - The loaded consumption response, for the period dates and the "now" instant.
 * @param isCurrentMonth - Whether the response is for the current month.
 * @returns The comparison figure in the vendor's currency, or null when there is none.
 */
function vendorComparisonAmount(vendor: VendorConsumptionResponse, response: MyConsumptionResponse, isCurrentMonth: boolean): number | null {
  if (vendor.costAmount === null) {
    return null;
  }
  if (!isCurrentMonth) {
    return vendor.costAmount;
  }
  const weekdayRatePerDay = vendor.weekdayRatePerDay ?? vendor.burnRatePerDay;
  const weekendRatePerDay = vendor.weekendRatePerDay ?? vendor.burnRatePerDay;
  if (typeof weekdayRatePerDay !== "number" || typeof weekendRatePerDay !== "number") {
    return null;
  }
  return projectMonthEnd({
    periodStart: response.periodStart,
    periodEnd: response.periodEnd,
    generatedAt: response.generatedAt,
    amount: vendor.costAmount,
    weekdayRatePerDay,
    weekendRatePerDay,
  });
}

/** A dash in place of a figure a tool does not report, read out as "Not reported". */
function NotReported(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <dd className={styles.notReported}>
      <span aria-hidden="true">—</span>
      <span className="visually-hidden">{t("consumption.vendor.notReported")}</span>
    </dd>
  );
}

/**
 * The provider grid's column counts, as custom properties for the stylesheet: the cards are spread evenly over as few rows as
 * hold at most three each (four make two rows of two, five a row of three and one of two), and at most two on a mid-width page.
 */
function vendorColumns(count: number): React.CSSProperties {
  const wide = Math.max(1, Math.ceil(count / Math.ceil(count / 3)));
  return { "--vendor-columns-wide": wide, "--vendor-columns-mid": Math.min(2, wide) } as React.CSSProperties;
}

/** A small clock beside the provider link, for a provider whose figures are behind; the tooltip says what that means. */
function BehindIndicator({ vendorName }: { vendorName: string }): React.JSX.Element {
  const { t } = useTranslation();
  const descriptionId = useId();
  return (
    <span className={styles.behind}>
      <span className={styles.behindIcon} role="img" tabIndex={0} aria-label={t("consumption.vendor.incomplete")} aria-describedby={descriptionId}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      </span>
      <span className={styles.behindTooltip} aria-hidden="true">
        <span className={styles.behindTooltipTitle}>{t("consumption.vendor.incomplete")}</span>
        <span>{t("consumption.vendor.incompleteDetail", { vendor: vendorName })}</span>
      </span>
      <span id={descriptionId} className="visually-hidden">
        {t("consumption.vendor.incompleteDetail", { vendor: vendorName })}
      </span>
    </span>
  );
}

function VendorCard({
  index,
  accentColor,
  vendor,
  response,
  dailySpend,
  loadingDailySpend,
  isCurrentMonth,
  previousPeriod,
}: {
  /** The card's place in the list, so the cards can appear one after another. */
  index: number;
  /** The vendor's colour in the forecast chart, or null when it has no series there. */
  accentColor: string | null;
  vendor: VendorConsumptionResponse;
  response: MyConsumptionResponse;
  dailySpend: MyDailySpendResponse | null;
  loadingDailySpend: boolean;
  isCurrentMonth: boolean;
  previousPeriod: HistoryPeriodResponse | null;
}): React.JSX.Element {
   const { t } = useTranslation();
   const { snapshot } = useSession();
   const allocation = response.allowances.find((line) => line.vendorId === vendor.vendorId);
   const previousVendor = previousPeriod?.vendors.find((line) => line.vendorId === vendor.vendorId);
   const previousTotal: PreviousTotal | null =
     previousPeriod && previousVendor ? { periodStart: previousPeriod.periodStart, total: previousVendor.costAmount, currency: previousVendor.costCurrency } : null;
   const comparisonAmount = vendorComparisonAmount(vendor, response, isCurrentMonth);
   // A flat-fee provider has no limit to measure against, and a meter needs both figures in one currency.
   const limitedAllocation = isCurrentMonth && allocation && !allocation.isFlatFee ? allocation : null;
   const meterAllowance =
     limitedAllocation && limitedAllocation.allowanceAmount > 0 && limitedAllocation.allowanceCurrency === vendor.costCurrency
       ? limitedAllocation.allowanceAmount
       : null;
   // The same rule as the forecast charts: a run-out on or after the month's last working day is not
   // a lapse (the allowance renews on the 1st), so it is not shown; a past one is, in the past tense.
   const todayKey = todayKeyFor(response.periodStart, response.periodEnd, response.generatedAt);
   const runOutDate = isCurrentMonth ? visibleRunOutDate(vendor.projectedExhaustionDate, response.periodEnd, todayKey) : null;
   // Estimated spend exists only for Bedrock; every other vendor renders as before.
   const estimated = vendor.estimatedCostAmount ?? 0;
   const hasEstimate = vendor.vendorId.toLowerCase() === "bedrock" && estimated > 0;
   const canSeeOperationalWarnings =
     snapshot.status === "signed-in" &&
     [
       "consumption.read.org",
       "consumption.read.others",
       "identity.impersonate",
       "settings.write",
       "audit.read",
       "roles.administer",
       "entitlement.administer",
       "accounts.administer",
       "request.approve",
     ].some((permission) => snapshot.permissions.includes(permission));

   return (
     <li
       className={styles.vendorCard}
       style={{ "--card-index": index, ...(accentColor ? { "--vendor-accent": accentColor } : {}) } as React.CSSProperties}
       data-spotlight
     >
       <div className={styles.cardActions}>
         {canSeeOperationalWarnings && vendor.isIncomplete && <BehindIndicator vendorName={vendor.displayName} />}
         <HoverTooltip label={t("consumption.vendor.openInVendor", { vendor: vendor.displayName })} align="end">
           <a
             className={styles.deepLink}
             href={vendor.deepLinkUrl}
             target="_blank"
             rel="noreferrer"
             aria-label={t("consumption.vendor.openInVendor", { vendor: vendor.displayName })}
           >
             <span aria-hidden="true">↗</span>
           </a>
         </HoverTooltip>
       </div>
       <div className={styles.vendorHeader}>
         <h3 className={styles.vendorName}>
           <span className={styles.vendorIconTile}>
             <VendorIcon vendorId={vendor.vendorId} className={styles.vendorIcon} />
           </span>
           {vendor.displayName}
         </h3>
       </div>

      {vendor.hasReading && (vendor.costAmount === null || vendor.costCurrency === null) ? (
        <p className={styles.note}>{t("consumption.vendor.notConverted")}</p>
      ) : (
        <p className={styles.vendorAmount}>
          {vendor.costAmount !== null && vendor.costCurrency !== null ? (
            <span>{formatMoney(vendor.costAmount, vendor.costCurrency)}</span>
          ) : (
            <span className={styles.notReported}>
              <span aria-hidden="true">—</span>
              <span className="visually-hidden">{t("consumption.vendor.notReported")}</span>
            </span>
          )}
          {limitedAllocation && (<span className={styles.allowanceTotal}>{" / "}{formatMoney(limitedAllocation.allowanceAmount, limitedAllocation.allowanceCurrency)}</span>)}
        </p>
      )}
      {/* Every card keeps the same rows in the same places, whatever a tool reports, so figures
          line up across adjacent cards: a missing meter or comparison keeps its space, and a
          missing figure shows a dash. */}
      {meterAllowance !== null && vendor.costAmount !== null ? (
        // Decorative: the amount and allowance are stated in the text just above it.
        <div className={styles.vendorMeter} aria-hidden="true">
          <div
            className={vendor.costAmount >= meterAllowance ? `${styles.vendorMeterFill} ${styles.vendorMeterFull}` : styles.vendorMeterFill}
            style={{ width: `${String(Math.min(100, (vendor.costAmount / meterAllowance) * 100))}%` }}
          />
        </div>
      ) : (
        <div className={`${styles.vendorMeter} ${styles.reserved}`} aria-hidden="true" />
      )}
      <div className={styles.comparisonSlot}>
        {vendor.costCurrency !== null && comparisonAmount !== null && (
          <MonthComparison
            currentAmount={comparisonAmount}
            currentCurrency={vendor.costCurrency}
            previous={previousTotal}
            basis={isCurrentMonth ? "projected" : "actual"}
          />
        )}
      </div>

      <dl className={styles.vendorStats}>
        <div>
          <dt>{t("consumption.vendor.tokensLabel")}</dt>
          {vendor.tokens !== null ? (
            <dd>
              <HoverTooltip label={t("consumption.vendor.tokens", { count: formatCount(vendor.tokens) })}>{formatCompactCount(vendor.tokens)}</HoverTooltip>
            </dd>
          ) : (
            <NotReported />
          )}
        </div>
        <div>
          <dt>{t("consumption.vendor.requestsLabel")}</dt>
          {vendor.requests !== null ? <dd>{formatCompactCount(vendor.requests)}</dd> : <NotReported />}
        </div>
        <div>
          <dt>{t("consumption.vendor.averageLabel")}</dt>
          {vendor.burnRatePerDay !== null && vendor.costCurrency !== null ? (
            <dd>{formatMoney(vendor.burnRatePerDay, vendor.costCurrency)}</dd>
          ) : (
            <NotReported />
          )}
        </div>
      </dl>

      {!vendor.hasReading && <p className={styles.runOut}>{t("consumption.vendor.noUsage")}</p>}
      {vendor.hasReading && runOutDate && (
        <p className={styles.runOut}>
          {t(runOutDate < todayKey ? "consumption.period.ranOut" : "consumption.projectedExhaustion", { date: formatDate(runOutDate) })}
        </p>
      )}

      {vendor.burnRatePerDay !== null && (
        <VendorMiniChart
          accentColor={accentColor}
          response={response}
          vendor={vendor}
          dailySpend={dailySpend}
          loadingDailySpend={loadingDailySpend}
          isCurrentMonth={isCurrentMonth}
        />
      )}

      {/* Bedrock only: how much of the figure is confirmed by AWS billing and how much is still an estimate. It sits
          below the rows every card shares, so the Bedrock card still lines up with its neighbours. */}
      {hasEstimate && vendor.costCurrency !== null && (
        <div className={styles.estimate}>
          <ul className={styles.estimateFigures}>
            <li>{t("consumption.vendor.confirmedAmount", { amount: formatMoney((vendor.costAmount ?? 0) - estimated, vendor.costCurrency) })}</li>
            <li>{t("consumption.vendor.estimatedAmount", { amount: formatMoney(estimated, vendor.costCurrency) })}</li>
          </ul>
          <p className={styles.basis}>
            {vendor.costConfirmedThrough
              ? t("consumption.vendor.confirmedThrough", { date: formatDate(vendor.costConfirmedThrough) })
              : t("consumption.vendor.confirmedThroughNone")}
          </p>
          <p className={styles.basis}>{t("consumption.vendor.dailyConfirmedOnly")}</p>
        </div>
      )}

      {vendor.latestObservedAt && <p className={styles.age}>{t("consumption.vendor.asOf", { age: formatAge(vendor.latestObservedAt) })}</p>}
      </li>
  );
}

/**
 * A compact version of {@link UsageProjectionChart} scoped to a single vendor (daily spend, running
 * total and projected-to-month-end), so a person can see a tool's own trend without leaving its
 * card. Uses the same dual-axis approach and shared {@link buildForecastSeries} data so the
 * per-vendor chart never tells a different story than the consolidated one above it.
 */
function VendorMiniChart({
  accentColor,
  response,
  vendor,
  dailySpend,
  loadingDailySpend,
  isCurrentMonth,
}: {
  /** The vendor's colour in the forecast chart, or null when it has none; the chart is drawn in it. */
  accentColor: string | null;
  response: MyConsumptionResponse;
  vendor: VendorConsumptionResponse;
  dailySpend: MyDailySpendResponse | null;
  loadingDailySpend: boolean;
  isCurrentMonth: boolean;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const effects = useChartEffectIds();

  if (vendor.burnRatePerDay === null || vendor.costAmount === null) {
    return null;
  }

  const dailyAmounts = new Map<string, number>(
    (dailySpend?.days ?? []).map((day) => [
      day.date,
      day.vendors.find((v) => v.vendorId === vendor.vendorId)?.amount ?? 0,
    ]),
  );

  const { chartData, daysInPeriod, hasActualSpendSeries, chartReferenceX, todayX } = buildForecastSeries({
    periodStart: response.periodStart,
    periodEnd: response.periodEnd,
    generatedAt: response.generatedAt,
    amount: vendor.costAmount,
    weekdayRatePerDay: vendor.weekdayRatePerDay ?? vendor.burnRatePerDay,
    weekendRatePerDay: vendor.weekendRatePerDay ?? vendor.burnRatePerDay,
    projectedExhaustionDate: vendor.projectedExhaustionDate,
    dailyAmounts,
    seatFees: vendor.seatFeeAmount ?? 0,
  });

  if (!loadingDailySpend && !hasActualSpendSeries && !vendor.hasReading) {
    return null;
  }

  // The chart is drawn in the vendor's own colour, as its bars are in the forecast chart above. The running total and projection
  // are that colour moved towards the text colour, so they stand off the bars, and a light accent such as Saffron still shows on
  // a white panel. A vendor with no series in the forecast chart keeps the palette's first three colours.
  const barColor = accentColor ?? getSeriesColor(0);
  const totalColor = accentColor ? `color-mix(in srgb, ${accentColor} 72%, var(--color-text-primary))` : getSeriesColor(1);
  const projectedColor = accentColor ? totalColor : getSeriesColor(2);
  // The bars are a gradient, which cannot colour the tooltip's squares, so the tooltip is told every series' colour.
  const tooltipColors = new Map([
    ["actual", barColor],
    ["cumulative", totalColor],
    ["projected", projectedColor],
  ]);

  return (
    <div className={styles.miniChartContainer} role="img" aria-label={t("consumption.period.chartLabel")}>
      <ResponsiveContainer width="100%" height={120}>
        <ComposedChart data={chartData} margin={{ top: 16, right: 8, bottom: 0, left: 8 }}>
          <ChartDefs
            ids={effects}
            gradients={[
              { key: "actual", color: barColor, top: 0.9, bottom: 0.35 },
              { key: "cumulative", color: totalColor, top: 0.22, bottom: 0 },
            ]}
          />
          <XAxis dataKey="label" tick={{ fill: "var(--color-text-secondary)", fontSize: 10 }} interval={Math.max(0, Math.ceil(daysInPeriod / 3) - 1)} tickLine={false} axisLine={false} />
          <YAxis yAxisId="daily" hide />
          <YAxis yAxisId="total" orientation="right" hide />
          <Tooltip content={<ForecastTooltip currency={vendor.costCurrency ?? ""} colors={tooltipColors} />} cursor={{ fill: `color-mix(in srgb, ${barColor} 10%, transparent)` }} />
          {hasActualSpendSeries && (
            <Bar yAxisId="daily" dataKey="actual" name={t("consumption.period.actual")} fill={`url(#${effects.gradient("actual")})`} radius={[3, 3, 0, 0]} maxBarSize={10} />
          )}
          <Area
            yAxisId="total"
            type="monotone"
            dataKey="cumulative"
            name={t("consumption.period.actual")}
            stroke={totalColor}
            strokeWidth={2}
            fill={`url(#${effects.gradient("cumulative")})`}
            filter={`url(#${effects.glow})`}
            dot={false}
            connectNulls={false}
          />
          {isCurrentMonth && (
            <Line
              yAxisId="total"
              type="monotone"
              dataKey="projected"
              name={t("consumption.period.projected")}
              stroke={projectedColor}
              strokeWidth={2}
              strokeDasharray="5 5"
              strokeLinecap="round"
              filter={`url(#${effects.glow})`}
              dot={false}
            />
          )}
          {isCurrentMonth && <ReferenceLine yAxisId="total" x={todayX} stroke={accentColor ? `color-mix(in srgb, ${accentColor} 55%, transparent)` : getSeriesColor(2)} strokeDasharray="3 3" ifOverflow="extendDomain" />}
          {isCurrentMonth && chartReferenceX && (
            <ReferenceLine
              yAxisId="total"
              x={
                chartData[
                  Math.min(
                    chartData.length - 1,
                    chartData.findIndex((point) => point.day === chartReferenceX) >= 0
                      ? chartData.findIndex((point) => point.day === chartReferenceX)
                      : chartData.length - 1,
                  )
                ].label
              }
              stroke="var(--color-warning-text)"
              strokeDasharray="3 3"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
